/**
 * Rate confirmation acceptance — field names and payload type.
 * Used by: frontend (AvailableLoads, LoadRequestCard, NotificationsTray) and
 * backend accept flows (acceptLoadForPO, acceptLoadForBrokerPO, handleUnifiedLoadAcceptance).
 * Keep field names in sync with Firestore load/PO documents.
 */

/** Firestore field: timestamp when carrier confirmed rate confirmation (ISO string or Timestamp). */
export const RATE_CONFIRMATION_ACCEPTED_AT = 'rateConfirmationAcceptedAt';

/** Firestore field: carrier uid who confirmed. */
export const RATE_CONFIRMATION_ACCEPTED_BY = 'rateConfirmationAcceptedBy';

/** Firestore field: optional signature name from modal. */
export const RATE_CONFIRMATION_SIGNATURE_NAME = 'rateConfirmationSignatureName';

export interface RateConfirmationAcceptancePayload {
  acceptedAt: string;
  acceptedBy: string;
  signatureName?: string;
}

/** PO field: when set, shipper/broker approved carrier but carrier must confirm rate confirmation before assign (Phase 4B). */
export const APPROVED_CARRIER_PENDING_RATE_CONFIRMATION = 'approvedCarrierPendingRateConfirmation';

/**
 * Phase 5.1 / 6: Backward compatibility: Loads/POs accepted before this feature do not have rateConfirmationAcceptedAt.
 * Use this helper when displaying "Rate confirmation accepted at/by" so missing = null (treat as "accepted before feature" or hide).
 * Phase 6: Also returns signatureName when present (optional audit line). acceptedAt may be string or Firestore Timestamp.
 */
export function getRateConfirmationDisplay(
  record: Record<string, unknown> | null | undefined
): { acceptedAt: string; acceptedBy: string; signatureName?: string } | null {
  if (!record || typeof record !== 'object') return null;
  const at = record[RATE_CONFIRMATION_ACCEPTED_AT];
  const by = record[RATE_CONFIRMATION_ACCEPTED_BY];
  if (at == null || by == null || typeof by !== 'string') return null;
  const atStr =
    typeof at === 'string'
      ? at
      : typeof (at as { toDate?: () => Date })?.toDate === 'function'
        ? (at as { toDate: () => Date }).toDate().toISOString()
        : null;
  if (!atStr) return null;
  const sig = record[RATE_CONFIRMATION_SIGNATURE_NAME];
  const signatureName = typeof sig === 'string' && sig.trim() ? sig.trim() : undefined;
  return { acceptedAt: atStr, acceptedBy: by, signatureName };
}
